// --- START OF FILE ProjectDetail/ProjectHeader.js ---
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  ArrowLeft,
  Check,
  X,
  HardDrive,
  Slack,
  Trash2,
  FileText,
  Plus,
  Copy,
  Edit2,
  Rocket,
} from "lucide-react";
import ClientSelector from "../ClientSelector";
import ProjectLinkButton from "../ProjectLinkButton";
const statusTranslations = {
  proposition: "Proposition",
  in_progress: "En cours",
  completed: "Terminé",
};
const ProjectHeader = ({
  project,
  clients,
  employees,
  circles,
  roleFocusEntries,
  apiBaseUrl,
  user,
  onNameUpdate,
  onNumberUpdate,
  onVisionUpdate,
  onBriefUpdate,
  onClientChange,
  onCircleChange,
  onAddClientContact,
  onUpdateClientContactRole,
  onRemoveClientContact,
  onLinkUpdate,
  onHeroImageUpload,
  onHeroImageRemove,
  onAddProjectRoleFocus,
  onRemoveProjectRoleFocus,
  highlights = [],
  activeIteration,
  objectiveProgress,
  navigate,
}) => {
  const [editingName, setEditingName] = useState(false);
  const [tempName, setTempName] = useState("");
  const [editingNumber, setEditingNumber] = useState(false);
  const [tempNumber, setTempNumber] = useState("");
  const [editingVision, setEditingVision] = useState(false);
  const [tempVision, setTempVision] = useState("");
  const [editingBrief, setEditingBrief] = useState(false);
  const [tempBrief, setTempBrief] = useState("");
  const [selectedCircleId, setSelectedCircleId] = useState("");
  const fileInputRef = useRef(null);
  const heroPressTimerRef = useRef(null);
  const heroBackgroundRef = useRef(null);
  const hoverShowTimerRef = useRef(null);
  const tooltipHideTimerRef = useRef(null);
  const [selectedContactId, setSelectedContactId] = useState("");
  const [newContactRole, setNewContactRole] = useState("");
  const [roleDrafts, setRoleDrafts] = useState({});
  const [copiedContactId, setCopiedContactId] = useState(null);
  const [editingContactRoleId, setEditingContactRoleId] = useState(null);
  const [isAddingContactInline, setIsAddingContactInline] = useState(false);
  const [hoveredContactId, setHoveredContactId] = useState(null);
  const [focusRoleId, setFocusRoleId] = useState("");
  const [focusMemberId, setFocusMemberId] = useState("");
  const [focusSaving, setFocusSaving] = useState(false);
  const [showFocusPopover, setShowFocusPopover] = useState(false);
  const focusPopoverRef = useRef(null);
  const [inlineFocusRoleId, setInlineFocusRoleId] = useState(null);
  const [inlineFocusMemberId, setInlineFocusMemberId] = useState("");
  const [inlineFocusSaving, setInlineFocusSaving] = useState(false);
  const focusHoldTimerRef = useRef(null);
  const focusHoldKeyRef = useRef(null);
  useEffect(() => {
    setTempName(project?.name || "");
    setTempNumber(project?.project_number || "");
    setTempVision(project?.vision || "");
    setTempBrief(project?.brief || "");
    setSelectedCircleId(project?.circle_id?.toString() || "");
  }, [project]);
  useEffect(() => {
    const drafts = {};
    (project?.client_contacts || []).forEach((contact) => {
      drafts[contact.contact_id] = contact.role_in_project || "";
    });
    setRoleDrafts(drafts);
    setEditingContactRoleId(null);
  }, [project?.client_contacts]);
  useEffect(() => {
    setSelectedContactId("");
    setNewContactRole("");
    setIsAddingContactInline(false);
  }, [project?.client_id]);
  useEffect(() => {
    setFocusRoleId("");
    setFocusMemberId("");
  }, [project?.id]);
  useEffect(() => {
    setInlineFocusRoleId(null);
    setInlineFocusMemberId("");
  }, [project?.id]);
  useEffect(() => {
    setFocusMemberId("");
  }, [focusRoleId]);
  useEffect(() => {
    setInlineFocusMemberId("");
  }, [inlineFocusRoleId]);
  useEffect(() => {
    if (!showFocusPopover) return;
    const handleClick = (event) => {
      if (!focusPopoverRef.current) return;
      if (!focusPopoverRef.current.contains(event.target)) {
        setShowFocusPopover(false);
      }
    };
    const handleKey = (event) => {
      if (event.key === "Escape") {
        setShowFocusPopover(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [showFocusPopover]);
  const cancelFocusHold = useCallback(() => {
    if (focusHoldTimerRef.current) {
      clearTimeout(focusHoldTimerRef.current);
      focusHoldTimerRef.current = null;
    }
    focusHoldKeyRef.current = null;
  }, []);
  const startFocusHold = useCallback(
    (roleId, employeeId) => {
      if (!onRemoveProjectRoleFocus || !roleId || !employeeId) return;
      cancelFocusHold();
      const key = `${roleId}:${employeeId}`;
      focusHoldKeyRef.current = key;
      focusHoldTimerRef.current = window.setTimeout(() => {
        focusHoldTimerRef.current = null;
        focusHoldKeyRef.current = null;
        onRemoveProjectRoleFocus(roleId, employeeId);
      }, 650);
    },
    [cancelFocusHold, onRemoveProjectRoleFocus],
  );
  useEffect(() => () => cancelFocusHold(), [cancelFocusHold]);
  useEffect(() => {
    if (!inlineFocusRoleId) return;
    const handleClick = (event) => {
      const container = document.querySelector(
        `[data-role-focus-container="${inlineFocusRoleId}"]`,
      );
      if (container && container.contains(event.target)) return;
      setInlineFocusRoleId(null);
    };
    const handleKey = (event) => {
      if (event.key === "Escape") {
        setInlineFocusRoleId(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [inlineFocusRoleId]);
  const projectContacts = Array.isArray(project?.client_contacts)
    ? project.client_contacts
    : [];
  const currentClient = useMemo(
    () => clients.find((clientItem) => clientItem.id === project?.client_id),
    [clients, project?.client_id],
  );
  const availableContacts = useMemo(() => {
    if (!currentClient || !Array.isArray(currentClient.contacts)) return [];
    const assignedIds = new Set(
      projectContacts.map((contact) => contact.contact_id),
    );
    return currentClient.contacts.filter(
      (contact) => !assignedIds.has(contact.id),
    );
  }, [currentClient, projectContacts]);
  const heroImageSrc = useMemo(() => {
    if (!project?.hero_image_url) return null;
    if (project.hero_image_url.startsWith("http"))
      return project.hero_image_url;
    if (project.hero_image_url.startsWith("/")) {
      return `${apiBaseUrl}${project.hero_image_url}`;
    }
    return project.hero_image_url;
  }, [project?.hero_image_url, apiBaseUrl]);
  const employeePictureById = useMemo(() => {
    const map = {};
    employees.forEach((emp) => {
      if (emp?.id) {
        map[emp.id] =
          emp.picture ||
          emp.photo_url ||
          emp.google_picture ||
          emp.google_picture_url ||
          null;
      }
    });
    return map;
  }, [employees]);
  const userIds = useMemo(() => {
    const ids = new Set();
    if (user?.id !== undefined && user?.id !== null) {
      ids.add(String(user.id));
    }
    if (user?.employee_id !== undefined && user?.employee_id !== null) {
      ids.add(String(user.employee_id));
    }
    return ids;
  }, [user]);
  const circleById = useMemo(() => {
    const map = {};
    (circles || []).forEach((circle) => {
      if (circle?.id === undefined || circle?.id === null) return;
      map[String(circle.id)] = circle;
    });
    return map;
  }, [circles]);
  const mentionPattern = /@\[(circle|role|project):(\d+)\|([^\]]+)\]/g;
  const formatFocusText = (text) =>
    (text || "").replace(mentionPattern, (_match, _type, _id, label) => `@${label}`);
  const projectRoleGroups = useMemo(() => {
    const projectToken =
      project?.id !== undefined && project?.id !== null
        ? `@[project:${project.id}|`
        : null;
    if (!projectToken) return [];
    const grouped = new Map();
    (roleFocusEntries || []).forEach((entry) => {
      if (!entry?.focus_text || !entry.focus_text.includes(projectToken)) return;
      const role = circleById[String(entry.circle_id)];
      const roleName = role?.name || `Role #${entry.circle_id}`;
      const employee =
        employees.find((emp) => emp.id === entry.employee_id) || null;
      const member = {
        key: `${entry.circle_id}:${entry.employee_id}`,
        employeeId: entry.employee_id,
        employee,
        focusText: formatFocusText(entry.focus_text || ""),
      };
      if (!grouped.has(entry.circle_id)) {
        grouped.set(entry.circle_id, {
          roleId: entry.circle_id,
          roleName,
          members: [member],
        });
      } else {
        grouped.get(entry.circle_id).members.push(member);
      }
    });
    const groups = Array.from(grouped.values());
    groups.forEach((group) => {
      group.members.sort((a, b) => {
        const nameA = a.employee?.name || "";
        const nameB = b.employee?.name || "";
        return nameA.localeCompare(nameB);
      });
    });
    return groups.sort((a, b) => a.roleName.localeCompare(b.roleName));
  }, [roleFocusEntries, employees, circleById, project?.id]);
  const circleChildrenByParent = useMemo(() => {
    const map = {};
    (circles || []).forEach((circle) => {
      if (!circle) return;
      const parentKey = circle.parent_id === null || circle.parent_id === undefined
        ? 'root'
        : String(circle.parent_id);
      if (!map[parentKey]) map[parentKey] = [];
      map[parentKey].push(circle);
    });
    return map;
  }, [circles]);
  const focusParentCircleId = useMemo(() => {
    if (project?.circle_id === null || project?.circle_id === undefined) {
      return null;
    }
    const circleId = Number(project.circle_id);
    if (Number.isNaN(circleId)) return null;
    const circle = circleById[String(circleId)];
    if (!circle) return circleId;
    const hasChildren = (circleChildrenByParent[String(circleId)] || []).length > 0;
    const isRoleCircleLocal =
      circle.parent_id !== null && circle.parent_id !== undefined && !hasChildren;
    return isRoleCircleLocal ? circle.parent_id : circleId;
  }, [project?.circle_id, circleById, circleChildrenByParent]);
  const parseOwnerIds = (rawValue) => {
    if (Array.isArray(rawValue)) return rawValue;
    if (typeof rawValue === "string") {
      try {
        const parsed = JSON.parse(rawValue);
        return Array.isArray(parsed) ? parsed : [];
      } catch (err) {
        return [];
      }
    }
    return [];
  };
  const roleOptions = useMemo(() => {
    if (!focusParentCircleId) return [];
    const children = circleChildrenByParent[String(focusParentCircleId)] || [];
    const roles = children.filter((circle) => {
      if (!circle?.id) return false;
      const hasChildren = (circleChildrenByParent[String(circle.id)] || []).length > 0;
      return circle.parent_id !== null && circle.parent_id !== undefined && !hasChildren;
    });
    return roles.slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [circleChildrenByParent, focusParentCircleId]);
  const getRoleMemberOptions = useCallback(
    (roleId) => {
      if (!roleId) return [];
      const roleCircle = circleById[String(roleId)];
      if (!roleCircle) return [];
      const roleOwnerIds = new Set();
      if (roleCircle.owner_id !== null && roleCircle.owner_id !== undefined) {
        roleOwnerIds.add(Number(roleCircle.owner_id));
      }
      const ownerIds = parseOwnerIds(roleCircle.owner_ids);
      ownerIds.forEach((id) => {
        const parsed = Number(id);
        if (!Number.isNaN(parsed)) roleOwnerIds.add(parsed);
      });
      if (!roleOwnerIds.size) return [];
      return (employees || [])
        .filter((emp) => roleOwnerIds.has(Number(emp.id)))
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    },
    [circleById, employees],
  );
  const selectedRole = useMemo(
    () => roleOptions.find((role) => String(role.id) === String(focusRoleId)) || null,
    [roleOptions, focusRoleId],
  );
  const roleMemberOptions = useMemo(() => {
    if (!selectedRole) return [];
    const roleOwnerIds = new Set();
    if (selectedRole.owner_id !== null && selectedRole.owner_id !== undefined) {
      roleOwnerIds.add(Number(selectedRole.owner_id));
    }
    const ownerIds = parseOwnerIds(selectedRole.owner_ids);
    ownerIds.forEach((id) => {
      const parsed = Number(id);
      if (!Number.isNaN(parsed)) roleOwnerIds.add(parsed);
    });
    if (!roleOwnerIds.size) return [];
    return (employees || [])
      .filter((emp) => roleOwnerIds.has(Number(emp.id)))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [employees, selectedRole]);
  const projectMentionPrefix = useMemo(
    () => (project?.id ? `@[project:${project.id}|` : null),
    [project?.id],
  );
  const selectedFocusEntry = useMemo(() => {
    if (!focusRoleId || !focusMemberId) return null;
    return (roleFocusEntries || []).find(
      (entry) =>
        String(entry?.circle_id) === String(focusRoleId) &&
        String(entry?.employee_id) === String(focusMemberId),
    );
  }, [roleFocusEntries, focusRoleId, focusMemberId]);
  const hasProjectFocus = Boolean(
    projectMentionPrefix &&
      typeof selectedFocusEntry?.focus_text === "string" &&
      selectedFocusEntry.focus_text.includes(projectMentionPrefix),
  );
  const canAddFocus =
    Boolean(onAddProjectRoleFocus) &&
    Boolean(focusRoleId) &&
    Boolean(focusMemberId) &&
    !focusSaving &&
    !hasProjectFocus;
  const inlineHasProjectFocus = Boolean(
    projectMentionPrefix &&
      inlineFocusRoleId &&
      inlineFocusMemberId &&
      (roleFocusEntries || []).some(
        (entry) =>
          String(entry?.circle_id) === String(inlineFocusRoleId) &&
          String(entry?.employee_id) === String(inlineFocusMemberId) &&
          typeof entry?.focus_text === "string" &&
          entry.focus_text.includes(projectMentionPrefix),
      ),
  );
  const canAddInlineFocus =
    Boolean(onAddProjectRoleFocus) &&
    Boolean(inlineFocusRoleId) &&
    Boolean(inlineFocusMemberId) &&
    !inlineFocusSaving &&
    !inlineHasProjectFocus;
  const handleAddFocus = async () => {
    if (!onAddProjectRoleFocus || !canAddFocus) return;
    const roleId = Number(focusRoleId);
    const memberId = Number(focusMemberId);
    if (Number.isNaN(roleId) || Number.isNaN(memberId)) return;
    setFocusSaving(true);
    const ok = await onAddProjectRoleFocus(roleId, memberId);
    setFocusSaving(false);
    if (ok) {
      setFocusMemberId("");
      setShowFocusPopover(false);
    }
  };
  const handleAddInlineFocus = async () => {
    if (!onAddProjectRoleFocus || !canAddInlineFocus) return;
    const roleId = Number(inlineFocusRoleId);
    const memberId = Number(inlineFocusMemberId);
    if (Number.isNaN(roleId) || Number.isNaN(memberId)) return;
    setInlineFocusSaving(true);
    const ok = await onAddProjectRoleFocus(roleId, memberId);
    setInlineFocusSaving(false);
    if (ok) {
      setInlineFocusMemberId("");
      setInlineFocusRoleId(null);
    }
  };
  const leaderCircleIds = useMemo(() => {
    const ids = new Set();
    if (!userIds.size) return ids;
    (circles || []).forEach((circle) => {
      if (!circle || circle.id === undefined || circle.id === null) return;
      const isLeader =
        (circle.owner_id !== undefined &&
          circle.owner_id !== null &&
          userIds.has(String(circle.owner_id))) ||
        (Array.isArray(circle.owner_ids) &&
          circle.owner_ids.some((id) => userIds.has(String(id))));
      if (isLeader) {
        ids.add(String(circle.id));
      }
    });
    return ids;
  }, [circles, userIds]);
  const eligibleLeaderIds = useMemo(() => {
    const ids = new Set();
    if (!leaderCircleIds.size) return ids;
    let currentId = project?.circle_id ?? null;
    while (currentId !== null && currentId !== undefined) {
      const key = String(currentId);
      if (leaderCircleIds.has(key)) {
        ids.add(key);
      }
      const parentId = circleById[key]?.parent_id ?? null;
      if (parentId === null || parentId === undefined) break;
      currentId = parentId;
    }
    return ids;
  }, [leaderCircleIds, project?.circle_id, circleById]);
  const canEditCircle = eligibleLeaderIds.size > 0;
  const allowedCircleIds = useMemo(() => {
    if (!canEditCircle) return new Set();
    const ids = new Set();
    const stack = Array.from(eligibleLeaderIds);
    while (stack.length) {
      const current = stack.pop();
      if (!current || ids.has(current)) continue;
      ids.add(current);
      const children = circleChildrenByParent[String(current)] || [];
      children.forEach((child) => {
        if (child?.id !== undefined && child?.id !== null) {
          stack.push(String(child.id));
        }
      });
    }
    return ids;
  }, [eligibleLeaderIds, circleChildrenByParent, canEditCircle]);
  const circleOptions = useMemo(() => {
    const list = circles || [];
    const filtered = canEditCircle
      ? list.filter((circle) => allowedCircleIds.has(String(circle.id)))
      : list.filter((circle) => String(circle.id) === String(project?.circle_id));
    return filtered.slice().sort((a, b) => {
      return (a.name || '').localeCompare(b.name || '');
    }).map((circle) => ({
      id: circle.id,
      label: circle.name || `Cercle #${circle.id}`,
    }));
  }, [circles, allowedCircleIds, canEditCircle, project?.circle_id]);
  const essentialLinks = useMemo(
    () => [
      {
        key: "drive",
        icon: HardDrive,
        label: "Google Drive",
        field: "google_drive_url",
        url: project?.google_drive_url,
      },
      {
        key: "slack",
        icon: Slack,
        label: "Slack",
        field: "slack_channel_url",
        url: project?.slack_channel_url,
      },
    ],
    [project?.google_drive_url, project?.slack_channel_url],
  );
  const handleNameEdit = () => {
    if (project?.name !== tempName) {
      onNameUpdate?.(tempName);
    }
    setEditingName(false);
  };
  const handleNumberEdit = () => {
    if (project?.project_number !== tempNumber) {
      onNumberUpdate?.(tempNumber);
    }
    setEditingNumber(false);
  };
  const handleVisionEdit = () => {
    if (project?.vision !== tempVision) {
      onVisionUpdate?.(tempVision);
    }
    setEditingVision(false);
  };
  const handleBriefSave = () => {
    if (project?.brief !== tempBrief) {
      onBriefUpdate?.(tempBrief);
    }
    setEditingBrief(false);
  };
  const handleCircleChange = (event) => {
    const nextValue = event.target.value;
    if (!canEditCircle) return;
    setSelectedCircleId(nextValue);
    if (project?.circle_id?.toString() !== nextValue) {
      onCircleChange?.(nextValue);
    }
  };

  const handleLinkEdit = (field, label) => {
    if (!onLinkUpdate) return;
    const currentValue = project?.[field] || "";
    const promptValue = window.prompt(`Lien ${label}`, currentValue);
    if (promptValue === null) return;
    const trimmed = promptValue.trim();
    onLinkUpdate(field, trimmed === "" ? null : trimmed);
  };
  const handleRoleChange = (contactId, value) => {
    setRoleDrafts((prev) => ({ ...prev, [contactId]: value }));
  };
  const handleRoleCommit = async (contactId) => {
    if (!onUpdateClientContactRole) return;
    const nextValue = (roleDrafts[contactId] || "").trim();
    const currentValue =
      projectContacts.find((contact) => contact.contact_id === contactId)
        ?.role_in_project || "";
    if (nextValue === currentValue) return;
    await onUpdateClientContactRole(contactId, nextValue);
    setEditingContactRoleId((current) =>
      current === contactId ? null : current,
    );
  };
  const handleRoleKeyDown = (event, contactId) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleRoleCommit(contactId);
    }
  };
  const startRoleEdit = (contact) => {
    setRoleDrafts((prev) => {
      if (prev[contact.contact_id] !== undefined) {
        return prev;
      }
      return { ...prev, [contact.contact_id]: contact.role_in_project || "" };
    });
    setEditingContactRoleId(contact.contact_id);
  };
  const handleAddContact = async (event) => {
    event.preventDefault();
    if (!selectedContactId || !onAddClientContact) return;
    const success = await onAddClientContact(
      Number(selectedContactId),
      newContactRole.trim() || "",
    );
    if (success) {
      setSelectedContactId("");
      setNewContactRole("");
      setIsAddingContactInline(false);
    }
  };

  useEffect(() => {
    if (availableContacts.length === 0) {
      setIsAddingContactInline(false);
    }
  }, [availableContacts.length]);
  const handleRemoveContact = async (contactId) => {
    await onRemoveClientContact?.(contactId);
  };
  const handleCopyEmail = async (contact) => {
    if (!contact.email) return;
    try {
      await navigator.clipboard.writeText(contact.email);
      setCopiedContactId(contact.contact_id);
      setTimeout(() => setCopiedContactId(null), 2000);
    } catch (err) {
      window.prompt("Copiez l'email du contact", contact.email);
    }
  };
  const handleContactMouseEnter = (contactId) => {
    if (tooltipHideTimerRef.current) {
      clearTimeout(tooltipHideTimerRef.current);
      tooltipHideTimerRef.current = null;
    }
    if (hoverShowTimerRef.current) {
      clearTimeout(hoverShowTimerRef.current);
    }
    hoverShowTimerRef.current = window.setTimeout(() => {
      setHoveredContactId(contactId);
      hoverShowTimerRef.current = null;
    }, 1000);
  };

  const handleContactMouseLeave = () => {
    if (hoverShowTimerRef.current) {
      clearTimeout(hoverShowTimerRef.current);
      hoverShowTimerRef.current = null;
    }
    if (tooltipHideTimerRef.current) {
      clearTimeout(tooltipHideTimerRef.current);
    }
    tooltipHideTimerRef.current = window.setTimeout(() => {
      setHoveredContactId(null);
      tooltipHideTimerRef.current = null;
    }, 500);
  };
  const handleFilePick = () => {
    fileInputRef.current?.click();
  };
  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file && onHeroImageUpload) {
      onHeroImageUpload(file);
    }
    event.target.value = "";
  };
  const shouldIgnoreHeroHold = (target) => {
    if (!target) return false;
    return Boolean(
      target.closest(
        "button, a, input, textarea, select, [contenteditable='true'], [data-hero-interactive='true']",
      ),
    );
  };
  const startHeroImageHold = (event) => {
    if (shouldIgnoreHeroHold(event.target)) return;
    if (heroPressTimerRef.current) {
      clearTimeout(heroPressTimerRef.current);
    }
    heroPressTimerRef.current = window.setTimeout(() => {
      heroPressTimerRef.current = null;
      handleFilePick();
    }, 800);
  };
  const stopHeroImageHold = () => {
    if (heroPressTimerRef.current) {
      clearTimeout(heroPressTimerRef.current);
      heroPressTimerRef.current = null;
    }
  };

  useEffect(
    () => () => {
      stopHeroImageHold();
    },
    [],
  );

  useEffect(
    () => () => {
      if (tooltipHideTimerRef.current) {
        clearTimeout(tooltipHideTimerRef.current);
      }
      if (hoverShowTimerRef.current) {
        clearTimeout(hoverShowTimerRef.current);
      }
    },
    [],
  );

  const driveLink = essentialLinks.find((link) => link.key === "drive");
  const slackLink = essentialLinks.find((link) => link.key === "slack");
  const statusKey =
    typeof project?.status === "string"
      ? project.status.toLowerCase()
      : null;
  const statusLabel =
    (statusKey && statusTranslations[statusKey]) ||
    project?.status_display ||
    (project?.status ? project.status.replace(/_/g, " ") : null);

  const navigateToKickoff = () => {
    if (project?.id) {
      navigate(`/project/${project.id}/kickoff`);
    }
  };
  const navigateToBacklog = () => {
    if (project?.id) {
      localStorage.setItem("objectiveBoard_viewMode", "project");
      localStorage.setItem(
        "objectiveBoard_selectedProject",
        project.id,
      );
    }
    navigate("/objectives");
  };

  const shortcutButtonClass =
    "inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/10 px-4 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70";
  return (
    <section
      className="relative w-full overflow-visible text-white"
      onMouseDownCapture={startHeroImageHold}
      onMouseUpCapture={stopHeroImageHold}
      onMouseLeave={stopHeroImageHold}
      onTouchStartCapture={startHeroImageHold}
      onTouchEndCapture={stopHeroImageHold}
      onTouchCancel={stopHeroImageHold}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />
      <div
        ref={heroBackgroundRef}
        className="absolute inset-0 overflow-hidden rounded-2xl shadow-xl"
        data-hero-background="true"
      >
        {heroImageSrc ? (
          <img
            src={heroImageSrc}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="h-full w-full bg-[radial-gradient(circle_at_top,_#1f2937,_#0f172a)]" />
        )}
        <div className="absolute inset-0 bg-slate-900/75" />
      </div>
      <div className="relative z-10 space-y-4 p-4 lg:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <button
              data-hero-interactive="true"
              onClick={() => navigate("/projects")}
              className="rounded-full border border-white/30 p-2 hover:bg-white/10"
              aria-label="Retour aux projets"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div
              className="flex flex-wrap items-center gap-2"
              data-hero-interactive="true"
            >
              {editingName ? (
                <input
                  value={tempName}
                  onChange={(e) => setTempName(e.target.value)}
                  onBlur={handleNameEdit}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleNameEdit();
                    if (e.key === "Escape") {
                      setEditingName(false);
                      setTempName(project?.name || "");
                    }
                  }}
                  autoFocus
                  className="max-w-xs rounded-full border border-white/50 bg-transparent px-3 py-1 text-sm font-semibold focus:border-white focus:outline-none"
                />
              ) : (
                <button
                  onClick={() => setEditingName(true)}
                  className="text-left text-lg font-semibold tracking-tight text-white hover:text-white/80"
                >
                  {project?.name || "Nom du projet"}
                </button>
              )}
              {editingNumber ? (
                <input
                  value={tempNumber}
                  onChange={(e) => setTempNumber(e.target.value)}
                  onBlur={handleNumberEdit}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleNumberEdit();
                    if (e.key === "Escape") {
                      setEditingNumber(false);
                      setTempNumber(project?.project_number || "");
                    }
                  }}
                  autoFocus
                  className="w-20 rounded-full border border-white/50 bg-transparent px-3 py-1 text-sm font-semibold focus:border-white focus:outline-none"
                />
              ) : (
                <button
                  onClick={() => setEditingNumber(true)}
                  className="rounded-full border border-white/30 px-3 py-1 text-xs font-semibold tracking-wide text-white/80 hover:border-white/60"
                >
                  {project?.project_number || "Projet ?"}
                </button>
              )}
              {circleOptions.length > 0 ? (
                <select
                  value={selectedCircleId}
                  onChange={handleCircleChange}
                  className="rounded-full border border-white/30 bg-transparent px-3 py-1 text-xs font-semibold tracking-wide text-white/80 hover:border-white/60 focus:border-white focus:outline-none disabled:opacity-70"
                  aria-label="Cercle du projet"
                  disabled={!canEditCircle}
                  title={!canEditCircle ? "Seul un leader du cercle ou d'un cercle supérieur peut modifier l'affectation." : undefined}
                >
                  {circleOptions.map((circle) => (
                    <option key={circle.id} value={circle.id} className="text-slate-900">
                      {circle.label}
                    </option>
                  ))}
                </select>
              ) : null}
              {statusLabel && (
                <span className="rounded-full border border-white/30 px-3 py-1 text-xs font-medium text-white/80">
                  {statusLabel}
                </span>
              )}
            </div>
          </div>
          <div
            className="flex flex-wrap items-center gap-2"
            data-hero-interactive="true"
          >
            <button
              onClick={navigateToKickoff}
              className={shortcutButtonClass}
            >
              <Rocket className="h-4 w-4" />
              Kickoff
            </button>
            <button
              onClick={navigateToBacklog}
              className={shortcutButtonClass}
            >
              <FileText className="h-4 w-4" />
              Backlog
            </button>
            {driveLink && (
              <ProjectLinkButton
                key={driveLink.key}
                icon={driveLink.icon}
                label={driveLink.label}
                url={driveLink.url}
                onEdit={() => handleLinkEdit(driveLink.field, driveLink.label)}
                size="sm"
                variant="dark"
                className="!rounded-full"
              />
            )}
            {slackLink && (
              <ProjectLinkButton
                key={slackLink.key}
                icon={slackLink.icon}
                label={slackLink.label}
                url={slackLink.url}
                onEdit={() => handleLinkEdit(slackLink.field, slackLink.label)}
                size="sm"
                variant="dark"
                className="!rounded-full"
              />
            )}
            {project?.hero_image_url && (
              <button
                onClick={onHeroImageRemove}
                className="inline-flex items-center justify-center rounded-full border border-white/30 bg-white/5 p-2 text-white/80 transition hover:bg-white/15"
                title="Retirer l'image"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 text-white/80">
          {editingVision ? (
            <input
              value={tempVision}
              onChange={(e) => setTempVision(e.target.value)}
              onBlur={handleVisionEdit}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleVisionEdit();
                if (e.key === "Escape") {
                  setEditingVision(false);
                  setTempVision(project?.vision || "");
                }
              }}
              autoFocus
              className="w-full max-w-xl rounded-full border border-white/40 bg-white/10 px-3 py-1 text-sm focus:border-white focus:outline-none"
            />
          ) : (
            <button
              onClick={() => setEditingVision(true)}
              className="text-left text-sm font-medium text-white/80 hover:text-white"
              title="Modifier la vision"
            >
              {project?.vision?.trim() || "Définir la vision du projet"}
            </button>
          )}
        </div>

        <div className="rounded-xl border border-white/15 bg-white/10 p-3 shadow-inner">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-white/70">
              Brief projet
            </p>
            {editingBrief ? (
              <div className="flex items-center gap-2 text-xs">
                <button
                  onClick={handleBriefSave}
                  className="inline-flex items-center gap-1 rounded-full bg-white/20 px-3 py-1 font-semibold hover:bg-white/30"
                >
                  <Check className="h-3.5 w-3.5" />
                  Sauvegarder
                </button>
                <button
                  onClick={() => {
                    setEditingBrief(false);
                    setTempBrief(project?.brief || "");
                  }}
                  className="inline-flex items-center gap-1 rounded-full border border-white/40 px-3 py-1 hover:border-white/60"
                >
                  <X className="h-3.5 w-3.5" />
                  Annuler
                </button>
              </div>
            ) : (
              <button
                onClick={() => setEditingBrief(true)}
                className="text-xs font-medium text-white/70 hover:text-white"
              >
                Modifier
              </button>
            )}
          </div>
          {editingBrief ? (
            <textarea
              value={tempBrief}
              onChange={(e) => setTempBrief(e.target.value)}
              rows={3}
              className="mt-2 w-full rounded-2xl border border-white/25 bg-black/10 px-3 py-2 text-sm text-white focus:border-white focus:outline-none"
            />
          ) : (
            <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-white/90">
              {project?.brief?.trim()
                ? project.brief
                : "Partagez un brief inspirant pour expliquer le contexte, les objectifs et le résultat attendu."}
            </p>
          )}
        </div>
        {Array.isArray(highlights) && highlights.length > 0 && (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
            {highlights.map((item) => (
              <div
                key={item.id}
                className="rounded-xl border border-white/15 bg-white/15 p-3 backdrop-blur-sm"
              >
                <p className="text-xs uppercase tracking-wide text-white/70">
                  {item.label}
                </p>
                <p
                  className={`mt-2 font-semibold text-white ${
                    item.id === "milestone"
                      ? "text-lg leading-snug"
                      : "text-xl"
                  }`}
                >
                  {item.value}
                </p>
                {item.helper && (
                  <p className="text-sm text-white/70">{item.helper}</p>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="grid gap-3 lg:grid-cols-2">
          <div className="rounded-xl bg-white/95 p-3 text-slate-900 shadow-lg">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Client
              </p>
              <ClientSelector
                projectId={project?.id}
                currentClient={currentClient}
                onClientChange={onClientChange}
                clients={clients}
              />
            </div>
            <div className="mt-2">
              {currentClient ? (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    {availableContacts.length > 0 && !isAddingContactInline && (
                      <button
                        type="button"
                        onClick={() => setIsAddingContactInline(true)}
                        className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-slate-400"
                      >
                        Ajouter un contact
                      </button>
                    )}
                  </div>
                  {isAddingContactInline && (
                    <form onSubmit={handleAddContact} className="space-y-2">
                      <select
                        value={selectedContactId}
                        onChange={(e) => setSelectedContactId(e.target.value)}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                      >
                        <option value="">Selectionner un contact</option>
                        {availableContacts.map((contact) => (
                          <option key={contact.id} value={contact.id}>
                            {`${contact.first_name || ""} ${contact.last_name || ""}`.trim() ||
                              "Contact sans nom"}
                          </option>
                        ))}
                      </select>
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <input
                          type="text"
                          value={newContactRole}
                          onChange={(e) => setNewContactRole(e.target.value)}
                          placeholder="Role sur le projet (optionnel)"
                          className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                        />
                        <div className="flex items-center gap-2">
                          <button
                            type="submit"
                            disabled={!selectedContactId}
                            className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-slate-400"
                          >
                            <Plus className="h-4 w-4" />
                            Ajouter
                          </button>
                          <button
                            type="button"
                            onClick={() => setIsAddingContactInline(false)}
                            className="rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-600 hover:border-slate-400"
                          >
                            Annuler
                          </button>
                        </div>
                      </div>
                    </form>
                  )}
                  {projectContacts.length === 0 ? (
                    <p className="text-sm text-slate-500">
                      Aucun contact assigne a ce projet.
                    </p>
                  ) : (
                    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                      {projectContacts.map((contact) => {
                        const isEditingRole =
                          editingContactRoleId === contact.contact_id;
                        const fullName =
                          `${contact.first_name || ""} ${contact.last_name || ""}`.trim();
                        const roleLabel =
                          contact.role_in_project?.trim() ||
                          "Role a renseigner";
                        const tooltipVisible =
                          hoveredContactId === contact.contact_id;
                        return (
                          <div
                            key={contact.contact_id}
                            onMouseEnter={() =>
                              handleContactMouseEnter(contact.contact_id)
                            }
                            onMouseLeave={handleContactMouseLeave}
                            className="group relative overflow-visible"
                          >
                            <div className="relative rounded-xl border border-slate-200 bg-white p-2 transition hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-lg overflow-hidden">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <p className="font-semibold text-slate-800">
                                    {fullName || "Contact sans nom"}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    {roleLabel}
                                  </p>
                                </div>
                                <div
                                  className={`flex items-center gap-1 text-slate-400 transition-all duration-200 ${tooltipVisible
                                    ? "opacity-100 pointer-events-auto"
                                    : "opacity-0 pointer-events-none"
                                  }`}
                                >
                                  {contact.email && (
                                    <button
                                      type="button"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleCopyEmail(contact);
                                      }}
                                      className="rounded-full border border-slate-200 p-1 hover:text-slate-700"
                                      title={
                                        copiedContactId === contact.contact_id
                                          ? "Email copie !"
                                          : "Copier le mail"
                                      }
                                    >
                                      <Copy className="h-4 w-4" />
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      isEditingRole
                                        ? setEditingContactRoleId(null)
                                        : startRoleEdit(contact);
                                    }}
                                    className="rounded-full border border-slate-200 p-1 hover:text-slate-700"
                                    title="Modifier le role"
                                  >
                                    <Edit2 className="h-4 w-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleRemoveContact(contact.contact_id);
                                    }}
                                    className="rounded-full border border-slate-200 p-1 text-slate-500 hover:border-red-200 hover:text-red-600"
                                    title="Retirer ce contact"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                              {isEditingRole && (
                                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                                  <input
                                    type="text"
                                    value={
                                      roleDrafts[contact.contact_id] ??
                                      contact.role_in_project ??
                                      ""
                                    }
                                    onChange={(e) =>
                                      handleRoleChange(
                                        contact.contact_id,
                                        e.target.value,
                                      )
                                    }
                                    onKeyDown={(e) =>
                                      handleRoleKeyDown(e, contact.contact_id)
                                    }
                                    placeholder="Sponsor, decisionnaire, expert..."
                                    className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                                  />
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleRoleCommit(contact.contact_id)
                                      }
                                      className="rounded-full bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                                    >
                                      Valider
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        setEditingContactRoleId(null);
                                      }}
                                      className="rounded-full border border-slate-200 px-3 py-1.5 text-xs text-slate-600 hover:border-slate-400"
                                    >
                                      Annuler
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                            {(contact.email ||
                              contact.job_title ||
                              contact.comment) && (
                              <div
                                className={`absolute left-0 top-full z-50 w-64 pt-2 transition-opacity duration-200 ${tooltipVisible
                                  ? "pointer-events-auto opacity-100"
                                  : "pointer-events-none opacity-0"
                                }`}
                              >
                                <div className="rounded-xl border border-slate-200 bg-white/95 p-3 text-xs text-slate-600 shadow-lg">
                                  {contact.email && (
                                    <div className="flex items-center justify-between gap-2">
                                      <span className="truncate text-slate-700">
                                        {contact.email}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleCopyEmail(contact);
                                        }}
                                        className="pointer-events-auto text-[11px] font-semibold text-blue-600 hover:text-blue-800"
                                      >
                                        {copiedContactId === contact.contact_id
                                          ? "Copie !"
                                          : "Copier"}
                                      </button>
                                    </div>
                                  )}
                                  {contact.job_title && (
                                    <p className="mt-2 font-medium text-slate-700">
                                      {contact.job_title}
                                    </p>
                                  )}
                                  {contact.comment && (
                                    <p className="mt-1 text-slate-500">
                                      {contact.comment}
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-500">
                  Associez un client au projet pour selectionner ses contacts.
                </p>
              )}
            </div>
          </div>

          <div className="rounded-xl bg-white/95 p-3 text-slate-900 shadow-lg">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Roles du projet
              </p>
              <div className="relative" ref={focusPopoverRef}>
                <button
                  type="button"
                  onClick={() => setShowFocusPopover((prev) => !prev)}
                  disabled={roleOptions.length === 0}
                  className="rounded-full border border-slate-200 p-1 text-slate-500 hover:border-slate-300 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                  title={
                    roleOptions.length === 0
                      ? "Aucun role disponible"
                      : "Ajouter un focus"
                  }
                >
                  <Plus className="h-4 w-4" />
                </button>
                {showFocusPopover && (
                  <div className="absolute right-0 z-20 mt-2 w-72 rounded-xl border border-slate-200 bg-white p-3 shadow-lg">
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Ajouter un focus
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowFocusPopover(false)}
                        className="rounded-full border border-slate-200 p-1 text-slate-400 hover:text-slate-600"
                        title="Fermer"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {roleOptions.length === 0 ? (
                      <p className="mt-2 text-xs text-slate-500">
                        Aucun role directement rattache au cercle du projet.
                      </p>
                    ) : (
                      <>
                        <div className="mt-2 space-y-2">
                          <label className="block text-xs">
                            <span className="text-[10px] uppercase tracking-wide text-slate-400">
                              Role
                            </span>
                            <select
                              value={focusRoleId}
                              onChange={(event) =>
                                setFocusRoleId(event.target.value)
                              }
                              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                            >
                              <option value="">Selectionner un role</option>
                              {roleOptions.map((role) => (
                                <option key={role.id} value={role.id}>
                                  {role.name || `Role #${role.id}`}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="block text-xs">
                            <span className="text-[10px] uppercase tracking-wide text-slate-400">
                              Participant
                            </span>
                            <select
                              value={focusMemberId}
                              onChange={(event) =>
                                setFocusMemberId(event.target.value)
                              }
                              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                              disabled={!focusRoleId || roleMemberOptions.length === 0}
                            >
                              <option value="">
                                {focusRoleId
                                  ? "Selectionner un participant"
                                  : "Selectionner un role"}
                              </option>
                              {roleMemberOptions.map((member) => (
                                <option key={member.id} value={member.id}>
                                  {member.name || `Collaborateur #${member.id}`}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>
                        {focusRoleId && roleMemberOptions.length === 0 && (
                          <p className="mt-2 text-xs text-slate-500">
                            Aucun participant n'est assigne a ce role.
                          </p>
                        )}
                        {hasProjectFocus && (
                          <p className="mt-2 text-xs text-emerald-600">
                            Ce participant a deja un focus sur ce projet.
                          </p>
                        )}
                        <div className="mt-3 flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => setShowFocusPopover(false)}
                            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-600 hover:border-slate-300"
                          >
                            Annuler
                          </button>
                          <button
                            type="button"
                            onClick={handleAddFocus}
                            disabled={!canAddFocus}
                            className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-400"
                          >
                            {focusSaving ? "Ajout..." : "Ajouter"}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3">
              {projectRoleGroups.length === 0 ? (
                <p className="text-sm text-slate-500">
                  Aucun role avec focus sur ce projet pour le moment.
                </p>
              ) : (
                <div className="grid gap-3 md:grid-cols-2">
                  {projectRoleGroups.map((role) => (
                    (() => {
                      const roleMemberOptions = getRoleMemberOptions(role.roleId);
                      return (
                    <div
                      key={role.roleId}
                      className="rounded-xl border border-slate-200 bg-white p-3"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-slate-800">
                          {role.roleName}
                        </p>
                        <div
                          className="relative"
                          data-role-focus-container={role.roleId}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setInlineFocusRoleId((current) =>
                                String(current) === String(role.roleId)
                                  ? null
                                  : role.roleId,
                              );
                            }}
                            className="rounded-full border border-slate-200 p-1 text-slate-500 hover:border-slate-300 hover:text-slate-700"
                            title="Ajouter un focus"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                          {String(inlineFocusRoleId) === String(role.roleId) && (
                            <div className="absolute right-0 z-20 mt-2 w-60 rounded-xl border border-slate-200 bg-white p-3 shadow-lg">
                              <div className="flex items-center justify-between">
                                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                  Ajouter un focus
                                </p>
                                <button
                                  type="button"
                                  onClick={() => setInlineFocusRoleId(null)}
                                  className="rounded-full border border-slate-200 p-1 text-slate-400 hover:text-slate-600"
                                  title="Fermer"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                              <label className="mt-2 block text-xs">
                                <span className="text-[10px] uppercase tracking-wide text-slate-400">
                                  Participant
                                </span>
                                <select
                                  value={inlineFocusMemberId}
                                  onChange={(event) =>
                                    setInlineFocusMemberId(event.target.value)
                                  }
                                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                                >
                                  <option value="">Selectionner</option>
                                  {roleMemberOptions.map(
                                    (member) => (
                                      <option key={member.id} value={member.id}>
                                        {member.name ||
                                          `Collaborateur #${member.id}`}
                                      </option>
                                    ),
                                  )}
                                </select>
                              </label>
                              {roleMemberOptions.length === 0 && (
                                <p className="mt-2 text-xs text-slate-500">
                                  Aucun participant n'est assigne a ce role.
                                </p>
                              )}
                              {inlineHasProjectFocus && (
                                <p className="mt-2 text-xs text-emerald-600">
                                  Ce participant a deja un focus sur ce projet.
                                </p>
                              )}
                              <div className="mt-3 flex justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => setInlineFocusRoleId(null)}
                                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-600 hover:border-slate-300"
                                >
                                  Annuler
                                </button>
                                <button
                                  type="button"
                                  onClick={handleAddInlineFocus}
                                  disabled={!canAddInlineFocus}
                                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-400"
                                >
                                  {inlineFocusSaving ? "Ajout..." : "Ajouter"}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-3">
                        {role.members.map((member) => {
                          const participantPicture =
                            member.employee?.picture ||
                            employeePictureById[member.employee?.id] ||
                            null;
                          const initials = member.employee?.name
                            ? member.employee.name
                                .split(" ")
                                .map((n) => n[0])
                                .join("")
                                .toUpperCase()
                            : "?";
                          return (
                            <div
                              key={member.key}
                              className="flex items-start gap-2"
                              title="Maintenir pour retirer le focus"
                              data-hero-interactive="true"
                              onMouseDown={() =>
                                startFocusHold(role.roleId, member.employeeId)
                              }
                              onMouseUp={cancelFocusHold}
                              onMouseLeave={cancelFocusHold}
                              onTouchStart={() =>
                                startFocusHold(role.roleId, member.employeeId)
                              }
                              onTouchEnd={cancelFocusHold}
                              onTouchCancel={cancelFocusHold}
                              onTouchMove={cancelFocusHold}
                            >
                              {participantPicture ? (
                                <img
                                  src={participantPicture}
                                  alt={member.employee?.name || ""}
                                  className="h-8 w-8 rounded-full border border-slate-200 object-cover"
                                />
                              ) : (
                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-600">
                                  {initials}
                                </div>
                              )}
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-800">
                                  {member.employee?.name || "Collaborateur"}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                      );
                    })()
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
export default ProjectHeader;
// --- END OF FILE ProjectDetail/ProjectHeader.js ---
